import { useState, useCallback, useMemo, useRef, Suspense, lazy } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { FunctionReference } from "convex/server";
import {
  ArrowLeft,
  Download,
  RotateCcw,
  AlertCircle,
  Loader2,
  FileText,
  FileOutput,
  CheckCircle2,
  ChevronDown,
  Save,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/layout";
import type { EditableDataGridHandle } from "@/components/editable-data-grid";
import { ProcoreTemplateGrid } from "@/components/submittal/procore-template-grid";
import { ProcessingSpinner } from "@/components/processing-spinner";
import type { TrackerType } from "@/components/tracker-view-modal";
import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";
import {
  PdfModalPip,
  type PdfModalPipMode,
  type PdfModalPipSourceRef,
} from "@/components/pdf-modal-pip";
import { downloadFile, toCSV } from "@/lib/csv";
import { findExactColumnIndex } from "@/lib/table-utils";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { uploadTableRowsPayload } from "@/lib/table-rows-upload";
import { useRouteParam } from "@/lib/use-route-param";
import {
  GLOBAL_PROCESSING_COMPLETED,
  GLOBAL_PROCESSING_FAILED,
  GLOBAL_PROCESSING_PENDING,
  GLOBAL_PROCESSING_PROCESSING,
} from "@/lib/processing-states";
import { ActionLogProvider, useRecordActionLog } from "@/hooks/use-action-log-session";

import {
  useSubmittalData,
  type VersionType,
} from "@/features/spec-pilot/submittal/use-submittal-data";
import { useSubmittalColumnMap } from "@/features/spec-pilot/submittal/submittal-columns";
import { useSubmittalBulkActions } from "@/features/spec-pilot/submittal/use-submittal-bulk-actions";
import { useSubmittalCellRenderer } from "@/features/spec-pilot/submittal/use-submittal-cell-renderer";
import { getSubmittalStateGate } from "@/features/spec-pilot/submittal/submittal-state-gate";
import { SetupProgress } from "@/features/spec-pilot/submittal/setup-progress";
import { SubmittalConfigCard } from "@/features/spec-pilot/submittal/submittal-config-card";

export const Route = createFileRoute("/specPilot/$jobId_/submittal")({
  component: SubmittalViewerPage,
});

type TrackerCountItem = { trackerType: "warranty" | "lead_time" | "attic_stock" | "custom"; customTrackerName?: string };

type SpecPilotPdfExportApi = {
  getLatest: FunctionReference<
    "query",
    "public",
    { submittalId: Id<"specPilotSubmittals"> },
    {
      artifactId: Id<"exportArtifacts">;
      status: string;
      error?: string;
      generatedAt?: number;
      operationId?: Id<"operations">;
      options: unknown;
    } | null
  >;
  getDownloadUrl: FunctionReference<
    "query",
    "public",
    { submittalId: Id<"specPilotSubmittals"> },
    string | null
  >;
  generate: FunctionReference<
    "mutation",
    "public",
    { submittalId: Id<"specPilotSubmittals">; version?: VersionType },
    { artifactId: Id<"exportArtifacts">; operationId: Id<"operations">; status: "pending" }
  >;
};

const specPilotPdfExportApi = (api as unknown as { specPilot: { pdfExport: SpecPilotPdfExportApi } })
  .specPilot.pdfExport;

function useSpecPilotPdfExport({
  enabled,
  submittalId,
  version,
  hasData,
  filenameBase,
}: {
  enabled: boolean;
  submittalId: Id<"specPilotSubmittals">;
  version: VersionType;
  hasData: boolean;
  filenameBase?: string;
}) {
  const generatePdfExport = useMutation(specPilotPdfExportApi.generate);
  const [isQueueingPdfExport, setIsQueueingPdfExport] = useState(false);
  const { data: pdfExport } = useQuery(
    convexQuery(specPilotPdfExportApi.getLatest, enabled ? { submittalId } : "skip")
  );
  const { data: pdfExportDownloadUrl, refetch: refetchPdfExportDownloadUrl } = useQuery(
    convexQuery(specPilotPdfExportApi.getDownloadUrl, enabled ? { submittalId } : "skip")
  );
  const pdfExportStatus = pdfExport?.status ?? "idle";
  const isPdfExportInProgress =
    isQueueingPdfExport ||
    pdfExportStatus === GLOBAL_PROCESSING_PENDING ||
    pdfExportStatus === GLOBAL_PROCESSING_PROCESSING;
  const canExportPdf = hasData && !isPdfExportInProgress;
  const canDownloadPdf =
    !isPdfExportInProgress &&
    Boolean(pdfExportDownloadUrl);

  const handleQueuePdfExport = useCallback(async () => {
    if (!enabled || !canExportPdf) return;
    setIsQueueingPdfExport(true);
    try {
      await generatePdfExport({ submittalId, version });
      toast.success("PDF export queued");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to queue PDF export");
      console.error("Failed to queue PDF export:", error);
    } finally {
      setIsQueueingPdfExport(false);
    }
  }, [enabled, canExportPdf, generatePdfExport, submittalId, version]);

  const handleDownloadPdf = useCallback(async () => {
    if (!enabled || !canDownloadPdf) return;
    try {
      const { data: refreshedUrl } = await refetchPdfExportDownloadUrl();
      const downloadUrl = refreshedUrl ?? pdfExportDownloadUrl;
      if (!downloadUrl) {
        toast.error("PDF export is not ready yet");
        return;
      }
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = `${filenameBase ?? "submittal"}-submittal.pdf`;
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      toast.success("Download started");
    } catch (error) {
      toast.error("Failed to download PDF");
      console.error("Failed to download PDF:", error);
    }
  }, [enabled, canDownloadPdf, refetchPdfExportDownloadUrl, pdfExportDownloadUrl, filenameBase]);

  return {
    pdfExport,
    pdfExportStatus,
    isPdfExportInProgress,
    canExportPdf,
    canDownloadPdf,
    handleQueuePdfExport,
    handleDownloadPdf,
  };
}

const TypicalActionsModalLazy = lazy(async () => {
  const mod = await import("@/components/typical-actions-modal");
  return { default: mod.TypicalActionsModal };
});

const TrackerViewModalLazy = lazy(async () => {
  const mod = await import("@/components/tracker-view-modal");
  return { default: mod.TrackerViewModal };
});

const MergeRowsModalLazy = lazy(async () => {
  const mod = await import("@/components/submittal/merge-rows-modal");
  return { default: mod.MergeRowsModal };
});

function SubmittalPdfControls({
  sourcePdf,
  sourceId,
  initialMode = "modal",
}: {
  sourcePdf: PdfModalPipSourceRef;
  sourceId: string;
  initialMode?: PdfModalPipMode;
}) {
  const [isPdfOpen, setIsPdfOpen] = useState(false);

  return (
    <>
      <Button
        className="fixed bottom-6 right-6 z-50 shadow-lg"
        onClick={() => setIsPdfOpen(true)}
      >
        <FileText className="size-4" />
        View Full PDF
      </Button>
      <PdfModalPip
        open={isPdfOpen}
        onOpenChange={setIsPdfOpen}
        sourcePdf={sourcePdf}
        sourceId={sourceId}
        initialMode={initialMode}
      />
    </>
  );
}

function SubmittalExportMenu({
  pdfExportError,
  pdfExportStatus,
  isPdfExportInProgress,
  canExportPdf,
  canDownloadPdf,
  onExportPdf,
  onDownloadPdf,
  onDownloadCsv,
}: {
  pdfExportError?: string;
  pdfExportStatus: string;
  isPdfExportInProgress: boolean;
  canExportPdf: boolean;
  canDownloadPdf: boolean;
  onExportPdf: () => void;
  onDownloadPdf: () => void;
  onDownloadCsv: () => void;
}) {
  const actionLabel =
    pdfExportStatus === GLOBAL_PROCESSING_COMPLETED
      ? "Re-export PDF"
      : pdfExportStatus === GLOBAL_PROCESSING_FAILED
        ? "Retry export PDF"
        : isPdfExportInProgress
          ? "Exporting..."
          : "Export PDF";

  const ActionIcon = isPdfExportInProgress
    ? Loader2
    : pdfExportStatus === GLOBAL_PROCESSING_COMPLETED
      ? CheckCircle2
      : pdfExportStatus === GLOBAL_PROCESSING_FAILED
        ? AlertCircle
        : FileOutput;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Download className="size-4" />
          <span className="hidden sm:inline">Export</span>
          <ChevronDown className="size-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[160]">
        {pdfExportError ? (
          <div className="max-w-64 px-2 py-1.5 text-xs text-destructive">
            {pdfExportError}
          </div>
        ) : null}
        <DropdownMenuItem onClick={onExportPdf} disabled={!canExportPdf} className="gap-2">
          <ActionIcon className={`size-4${isPdfExportInProgress ? " animate-spin" : ""}`} />
          <span>{actionLabel}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDownloadPdf} disabled={!canDownloadPdf} className="gap-2">
          <Download className="size-4" />
          <span>Download PDF</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDownloadCsv} className="gap-2">
          <Download className="size-4" />
          <span>Download CSV</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SubmittalViewerPage() {
  const jobId = useRouteParam("jobId") ?? "";
  const typedJobId = jobId as Id<"specPilotSubmittals">;
  const { data: job } = useQuery(
    convexQuery(api.specPilot.submittals.get, jobId ? { submittalId: typedJobId } : "skip")
  );

  return (
    <ActionLogProvider
      projectId={job?.projectId ?? null}
      pilot="SpecPilot"
      targetKind="specPilotSubmittal"
      targetId={jobId}
    >
      <SubmittalViewerContent />
    </ActionLogProvider>
  );
}

function SubmittalViewerContent() {
  const jobId = useRouteParam("jobId") ?? "";
  const typedJobId = jobId as Id<"specPilotSubmittals">;

  const {
    job,
    submittal,
    typicals: _typicals,
    trackerItems,
    displayData,
    activeVersion,
    setActiveVersion,
    hasEditedData,
    isLoadingCSV,
    csvError,
    isHydratingEdited,
    editedPayloadError,
    editedData,
    setEditedData,
    setEditedPayloadError,
    generatedData,
  } = useSubmittalData(jobId);

  const recordActionLog = useRecordActionLog();

  const updateEditedData = useMutation(api.specPilot.generatedSubmittals.updateEditedData);
  const clearEditedData = useMutation(api.specPilot.generatedSubmittals.clearEditedData);
  const generateR2UploadUrl = useMutation(api.files.generateR2UploadUrl);
  const recordCsiCorrection = useMutation(api.specPilot.csiCorrections.record);
  const addToTracker = useMutation(api.specPilot.trackerItems.createMany);

  const [isReverting, setIsReverting] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const gridRef = useRef<EditableDataGridHandle>(null);
  const [activeTracker, setActiveTracker] = useState<{ type: TrackerType; customName?: string } | null>(null);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [reviewTimesConfirmed, setReviewTimesConfirmed] = useState(false);
  const [csiCodesVerified, setCsiCodesVerified] = useState(false);
  const [gridHasChanges, setGridHasChanges] = useState(false);
  const [gridIsSaving, setGridIsSaving] = useState(false);

  const { data: orgMembers } = useQuery(convexQuery(api.auth.listOrganizationMembersMinimal, {}));
  const { data: sourcePdf } = useQuery(
    convexQuery(
      api.specPilot.submittals.getSourcePdfUrl,
      job ? { submittalId: typedJobId } : "skip"
    )
  );
  const { data: locations } = useQuery(
    convexQuery(
      api.specPilot.customItems.listByProjectAndType,
      job?.projectId ? { projectId: job.projectId, type: "location" } : "skip"
    )
  );
  const { data: contractors } = useQuery(
    convexQuery(
      api.specPilot.customItems.listByProjectAndType,
      job?.projectId ? { projectId: job.projectId, type: "contractor" } : "skip"
    )
  );

  const { data: customTrackerNames } = useQuery(
    convexQuery(
      api.specPilot.trackerItems.listCustomTrackerNames,
      job ? { submittalId: typedJobId } : "skip"
    )
  );
  const createCustomItem = useMutation(api.specPilot.customItems.create);

  const [submittalManager, setSubmittalManager] = useState<string | null>(null);
  const [designReviewTime, setDesignReviewTime] = useState<string>("10");
  const [internalReviewTime, setInternalReviewTime] = useState<string>("0");

  const columns = useSubmittalColumnMap(displayData?.headers);

  const trackerCounts = useMemo(() => {
    const items: TrackerCountItem[] = trackerItems ?? [];
    const counts: Record<"warranty" | "lead_time" | "attic_stock", number> = {
      warranty: 0,
      lead_time: 0,
      attic_stock: 0,
    };
    for (const item of items) {
      if (item.trackerType !== "custom") {
        counts[item.trackerType] += 1;
      }
    }
    return counts;
  }, [trackerItems]);

  const saveEditedSnapshot = useCallback(
    async (headers: string[], rows: string[][]) => {
      if (!submittal) {
        throw new Error("No submittal to save");
      }

      const uploadedRows = await uploadTableRowsPayload({
        generateUploadUrl: async () =>
          await generateR2UploadUrl({
            prefix: "spec-pilot/edited-submittals",
            filename: `${submittal._id}-edited.json.gz`,
          }),
        headers,
        rows,
      });

      await updateEditedData({
        submittalId: submittal._id,
        headers,
        editedFileKey: uploadedRows.rowsStorageKey,
        editedFormat: uploadedRows.rowsFormat,
        editedRowCount: uploadedRows.rowCount,
        editedHeaderCount: headers.length,
        editedVersion: Date.now(),
      });

      setEditedData({ headers, rows });
      setEditedPayloadError(null);
    },
    [submittal, generateR2UploadUrl, updateEditedData, setEditedData, setEditedPayloadError]
  );

  const handleSave = useCallback(
    async (headers: string[], rows: string[][]) => {
      if (!submittal) {
        throw new Error("No submittal to save");
      }

      const csiCol = findExactColumnIndex(headers, [
        "CSI Code",
        "Submittal Spec Section Number",
      ]);
      const csiTitleIdx = findExactColumnIndex(headers, [
        "CSI Title",
        "Submittal Spec Section Description",
      ]);
      const sourceTableIdx = findExactColumnIndex(headers, ["Source Table"]);
      const titleIdx = findExactColumnIndex(headers, ["Title", "Submittal Title"]);
      const descIdx = findExactColumnIndex(headers, ["Description"]);

      if (csiCol >= 0 && displayData) {
        for (let i = 0; i < rows.length; i++) {
          const newCode = rows[i]?.[csiCol] ?? "";
          const oldCode = displayData.rows[i]?.[csiCol] ?? "";
          if (newCode && oldCode && newCode !== oldCode) {
            recordCsiCorrection({
              submittalId: typedJobId,
              title: titleIdx >= 0 ? (rows[i][titleIdx] ?? "") : "",
              itemFields: [
                titleIdx >= 0 ? rows[i][titleIdx] : "",
                descIdx >= 0 ? rows[i][descIdx] : "",
              ]
                .filter(Boolean)
                .join(" | "),
              originalCode: oldCode,
              correctedCode: newCode,
              correctedTitle: csiTitleIdx >= 0 ? (rows[i][csiTitleIdx] ?? "") : "",
              tableContext: sourceTableIdx >= 0 ? (rows[i][sourceTableIdx] ?? "") : "",
            }).catch((err) => {
              console.error("Failed to record CSI correction:", err);
            });
            setCsiCodesVerified(false);
          }
        }
      }

      await saveEditedSnapshot(headers, rows);
      await recordActionLog();
      setActiveVersion("edited");
      toast.success("Changes saved successfully");
    },
    [submittal, saveEditedSnapshot, recordCsiCorrection, displayData, typedJobId, setActiveVersion, recordActionLog]
  );

  const handleRevert = useCallback(async () => {
    if (!submittal) return;

    setIsReverting(true);
    try {
      await clearEditedData({ submittalId: submittal._id });
      await recordActionLog();
      setActiveVersion("generated");
      setEditedData(null);
      setEditedPayloadError(null);
      toast.success("Reverted to generated version");
    } catch (error) {
      toast.error("Failed to revert changes");
      console.error("Error reverting:", error);
    } finally {
      setIsReverting(false);
    }
  }, [submittal, clearEditedData, setActiveVersion, setEditedData, setEditedPayloadError, recordActionLog]);

  const handleDownload = useCallback(() => {
    if (!job || !displayData) return;

    const filename = `${job.name}-submittal${activeVersion === "edited" ? "-edited" : ""}.csv`;
    const csvContent = toCSV(displayData.headers, displayData.rows);
    downloadFile(csvContent, filename, "text/csv");
    toast.success("Download started");
  }, [job, displayData, activeVersion]);

  const {
    pdfExport,
    pdfExportStatus,
    isPdfExportInProgress,
    canExportPdf,
    canDownloadPdf,
    handleQueuePdfExport,
    handleDownloadPdf,
  } = useSpecPilotPdfExport({
    enabled: Boolean(job),
    submittalId: typedJobId,
    version: activeVersion,
    hasData: Boolean(displayData),
    filenameBase: job?.name,
  });

  const {
    handleBulkCSIAssign,
    handleBulkColumnAssign,
    handleSubmittalManagerChange,
  } = useSubmittalBulkActions({
    gridRef,
    selectedRows,
    setSelectedRows,
    csiCodeCol: columns.csiCode,
    csiTitleCol: columns.csiTitle,
    submittalManagerCol: columns.submittalManager,
    displayRows: displayData?.rows,
    packageSpecSectionNumberCol: columns.packageSpecSectionNumber,
    packageSpecSectionDescriptionCol: columns.packageSpecSectionDescription,
    submittalNumberCol: columns.submittalNumber,
  });

  const cellRenderer = useSubmittalCellRenderer({
    columns,
    gridRef,
    displayRows: displayData?.rows,
    projectId: job?.projectId,
    locations: locations ?? [],
    contractors: contractors ?? [],
    createCustomItem,
  });

  const handleAddToTracker = useCallback(
    async (trackerType: TrackerType, customTrackerName?: string) => {
      if (selectedRows.size === 0 || !displayData) {
        toast.error("No rows selected");
        return;
      }

      const items = Array.from(selectedRows)
        .filter((rowIdx) => displayData.rows[rowIdx] !== undefined)
        .map((rowIdx) => {
          const data: Record<string, string> = {};
          for (let i = 0; i < displayData.headers.length; i++) {
            data[displayData.headers[i]] = displayData.rows[rowIdx]?.[i] ?? "";
          }
          return data;
        });

      try {
        const result = await addToTracker({
          submittalId: typedJobId,
          trackerType,
          items,
          customTrackerName: trackerType === "custom" ? customTrackerName : undefined,
        });
        if (result && result.skipped > 0) {
          toast.info(`Added ${result.added} item(s), ${result.skipped} duplicate(s) skipped`);
        } else {
          toast.success(`Added ${result?.added ?? items.length} item(s) to tracker`);
        }
        await recordActionLog();
        setSelectedRows(new Set());
      } catch (error) {
        toast.error("Failed to add to tracker");
        console.error("Error adding to tracker:", error);
      }
    },
    [selectedRows, displayData, addToTracker, typedJobId, recordActionLog]
  );

  const handleDeleteRows = useCallback(async () => {
    if (!submittal || !displayData || selectedRows.size === 0) return;

    const filteredRows = displayData.rows.filter((_, index) => !selectedRows.has(index));
    const count = displayData.rows.length - filteredRows.length;

    if (count === 0) {
      setSelectedRows(new Set());
      return;
    }

    await saveEditedSnapshot(displayData.headers, filteredRows);
    await recordActionLog();
    setSelectedRows(new Set());
    setActiveVersion("edited");
    toast.success(`Deleted ${count} row${count !== 1 ? "s" : ""}`);
  }, [submittal, displayData, selectedRows, saveEditedSnapshot, setActiveVersion, recordActionLog]);

  const handleMergeRows = useCallback(
    async (mergedRow: string[]) => {
      if (!submittal || !displayData || selectedRows.size < 2) return;

      const sortedSelected = Array.from(selectedRows).sort((a, b) => a - b);
      const firstSelectedIdx = sortedSelected[0];

      const updatedRows = displayData.rows.filter((_, index) => !selectedRows.has(index));
      updatedRows.splice(firstSelectedIdx, 0, mergedRow);

      try {
        await saveEditedSnapshot(displayData.headers, updatedRows);
        await recordActionLog();
        setSelectedRows(new Set());
        setActiveVersion("edited");
        toast.success(`Merged ${selectedRows.size} rows into one`);
      } catch (error) {
        console.error("Failed to merge rows:", error);
        toast.error("Failed to merge rows. Please try again.");
      }
    },
    [submittal, displayData, selectedRows, saveEditedSnapshot, setActiveVersion, recordActionLog]
  );

  const pageHeaderActions = useMemo(
    () => (
      <div className="flex items-center gap-3">
        {displayData ? (
          <>
            <Badge variant="outline" className="text-sm font-medium">
              Procore Import Template
            </Badge>
            <span className="text-sm text-muted-foreground">{displayData.rows.length} rows</span>
          </>
        ) : null}
        {activeVersion === "edited" && <Badge variant="secondary">Edited Version</Badge>}
        <SubmittalExportMenu
          pdfExportError={pdfExport?.error}
          pdfExportStatus={pdfExportStatus}
          isPdfExportInProgress={isPdfExportInProgress}
          canExportPdf={canExportPdf}
          canDownloadPdf={canDownloadPdf}
          onExportPdf={() => void handleQueuePdfExport()}
          onDownloadPdf={() => void handleDownloadPdf()}
          onDownloadCsv={handleDownload}
        />
      </div>
    ),
    [
      displayData,
      activeVersion,
      handleDownload,
      pdfExport?.error,
      pdfExportStatus,
      isPdfExportInProgress,
      canExportPdf,
      canDownloadPdf,
      handleQueuePdfExport,
      handleDownloadPdf,
    ]
  );

  const gridToolbar = useMemo(
    () => (
      <div className="flex flex-wrap items-center justify-between gap-4">
        {hasEditedData ? (
          <Tabs value={activeVersion} onValueChange={(v) => setActiveVersion(v as VersionType)}>
            <TabsList>
              <TabsTrigger value="generated">Generated</TabsTrigger>
              <TabsTrigger value="edited">Edited</TabsTrigger>
            </TabsList>
          </Tabs>
        ) : null}

        <div className="ml-auto flex flex-wrap items-center gap-3">
          {gridHasChanges && (
            <>
              <Button variant="outline" size="sm" onClick={() => gridRef.current?.cancel()} disabled={gridIsSaving}>
                <X className="size-4" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={async () => {
                  try {
                    await gridRef.current?.save();
                  } catch (error) {
                    console.error("Failed to save grid changes:", error);
                    toast.error("Failed to save changes");
                  }
                }}
                disabled={gridIsSaving}
              >
                {gridIsSaving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                {gridIsSaving ? "Saving..." : "Save Changes"}
              </Button>
            </>
          )}
          {hasEditedData ? (
            <DeleteConfirmationDialog
              title="Revert to generated version?"
              description="This will discard all your edits and restore the original generated submittal. This action cannot be undone."
              onConfirm={handleRevert}
              disabled={isReverting}
              confirmLabel={isReverting ? "Reverting..." : "Revert"}
            >
              <Button variant="outline" size="sm" disabled={isReverting}>
                {isReverting ? <Loader2 className="size-4 animate-spin" /> : <RotateCcw className="size-4" />}
                Revert to Generated
              </Button>
            </DeleteConfirmationDialog>
          ) : null}

          <Suspense fallback={<Button size="sm" variant="outline" disabled>Loading Actions...</Button>}>
            <TypicalActionsModalLazy submittalId={typedJobId} />
          </Suspense>
        </div>
      </div>
    ),
    [hasEditedData, activeVersion, gridHasChanges, gridIsSaving, isReverting, handleRevert, typedJobId]
  );

  const stateGate = getSubmittalStateGate({
    jobId,
    job,
    submittal,
    isLoadingCSV,
    generatedData,
    editedData,
    csvError,
    isHydratingEdited,
    editedPayloadError,
    activeVersion,
    hasEditedData,
  });

  if (stateGate) {
    return stateGate;
  }

  if (!job || !submittal) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Submittal"
          breadcrumbs={[{ label: "SpecPilot", href: "/specPilot" }, { label: "Submittal" }]}
        />
        <ProcessingSpinner message="Loading submittal data..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Submittal"
        breadcrumbs={[
          { label: "SpecPilot", href: "/specPilot" },
          { label: job.name, href: "/specPilot/$jobId", params: { jobId: job._id } },
          { label: "Submittal" },
        ]}
        actions={pageHeaderActions}
      />

      <SetupProgress
        managerSelected={submittalManager !== null}
        reviewsConfirmed={reviewTimesConfirmed}
        csiCodesVerified={csiCodesVerified}
      />

      <SubmittalConfigCard
        orgMembers={orgMembers ?? []}
        selectedRowCount={selectedRows.size}
        submittalManager={submittalManager}
        designReviewTime={designReviewTime}
        internalReviewTime={internalReviewTime}
        trackerCounts={trackerCounts}
        customTrackerNames={customTrackerNames ?? []}
        csiCodeCol={columns.csiCode}
        locationCol={columns.location}
        locations={locations ?? []}
        projectId={job.projectId}
        submittalStatusCol={columns.submittalStatus}
        submittalTypeCol={columns.submittalType}
        reviewTimesConfirmed={reviewTimesConfirmed}
        csiCodesVerified={csiCodesVerified}
        onSubmittalManagerChange={(managerName) => {
          setSubmittalManager(managerName);
          handleSubmittalManagerChange(managerName);
        }}
        onDesignReviewTimeChange={(value) => {
          setDesignReviewTime(value);
          setReviewTimesConfirmed(false);
          if (selectedRows.size > 0) {
            handleBulkColumnAssign(columns.designReviewTime, value, "Design Team Review Time");
          }
        }}
        onInternalReviewTimeChange={(value) => {
          setInternalReviewTime(value);
          setReviewTimesConfirmed(false);
          if (selectedRows.size > 0) {
            handleBulkColumnAssign(columns.internalReviewTime, value, "Internal Review Time");
          }
        }}
        onConfirmReviewTimes={() => setReviewTimesConfirmed(true)}
        onVerifyCSICodes={() => setCsiCodesVerified(true)}
        onTrackerClick={(type, customName) => setActiveTracker({ type, customName })}
        onBulkCSIAssign={(...args) => {
          setCsiCodesVerified(false);
          return handleBulkCSIAssign(...args);
        }}
        onBulkColumnAssign={handleBulkColumnAssign}
        createCustomItem={createCustomItem}
        onAddToTracker={handleAddToTracker}
        onCreateCustomTracker={async (name) => {
          if (selectedRows.size > 0) {
            await handleAddToTracker("custom", name);
            toast.success(`Custom tracker "${name}" created with ${selectedRows.size} item(s)!`);
          } else {
            toast.success(`Custom tracker "${name}" created! Select rows and use "Add to Tracker" to add items.`);
          }
        }}
        onDeleteRows={handleDeleteRows}
        onMergeRows={() => setIsMergeModalOpen(true)}
        onClearSelection={() => setSelectedRows(new Set())}
      />

      {gridToolbar}

      {editedPayloadError && activeVersion === "edited" ? (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Failed to load edited data</AlertTitle>
          <AlertDescription>{editedPayloadError}</AlertDescription>
        </Alert>
      ) : null}

      {displayData ? (
        <ProcoreTemplateGrid
          ref={gridRef}
          key={`${activeVersion}-${submittal._id}`}
          headers={displayData.headers}
          rows={displayData.rows}
          onSave={handleSave}
          selectable
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          cellRenderer={cellRenderer}
          packageColumnIndex={columns.packageTitle}
          showHeader={false}
          hideActionBar
          onHasChangesChange={setGridHasChanges}
          onIsSavingChange={setGridIsSaving}
        />
      ) : (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          No data available
        </div>
      )}

      <Button asChild variant="outline">
        <Link to="/specPilot/$jobId" params={{ jobId }}>
          <ArrowLeft className="size-4" />
          Back to Job
        </Link>
      </Button>

      {activeTracker !== null ? (
        <Suspense fallback={null}>
          <TrackerViewModalLazy
            submittalId={typedJobId}
            trackerType={activeTracker.type}
            customTrackerName={activeTracker.customName}
            isOpen
            onClose={() => setActiveTracker(null)}
          />
        </Suspense>
      ) : null}

      {selectedRows.size >= 2 && displayData ? (
        <Suspense fallback={null}>
          <MergeRowsModalLazy
            open={isMergeModalOpen}
            onOpenChange={setIsMergeModalOpen}
            headers={displayData.headers}
            selectedRows={Array.from(selectedRows)
              .sort((a, b) => a - b)
              .map((idx) => displayData.rows[idx])
              .filter((row): row is string[] => row !== undefined)}
            csiCodeCol={columns.csiCode}
            csiTitleCol={columns.csiTitle}
            packageSpecSectionNumberCol={columns.packageSpecSectionNumber}
            packageSpecSectionDescriptionCol={columns.packageSpecSectionDescription}
            locationCol={columns.location}
            submittalNumberCol={columns.submittalNumber}
            locations={locations ?? []}
            projectId={job?.projectId}
            createCustomItem={createCustomItem}
            onMerge={handleMergeRows}
          />
        </Suspense>
      ) : null}

      {sourcePdf?.fileUrl ? (
        <SubmittalPdfControls sourcePdf={sourcePdf} sourceId={typedJobId} />
      ) : null}
    </div>
  );
}
